import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Send, Calendar, Settings as SettingsIcon, CheckCircle, Lock, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      toast.error('فشل تحميل الإعدادات');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/settings`, settings);
      toast.success('تم حفظ الإعدادات بنجاح');
    } catch (error) {
      toast.error('فشل حفظ الإعدادات');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) {
      toast.error('الرجاء إدخال كلمة المرور الحالية والجديدة');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('كلمة المرور الجديدة غير متطابقة');
      return;
    }
    
    if (newPassword.length < 4) {
      toast.error('كلمة المرور يجب أن تكون 4 أحرف على الأقل');
      return;
    }

    try {
      setChangingPassword(true);
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      toast.success('تم تغيير كلمة المرور بنجاح');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('كلمة المرور الحالية غير صحيحة');
      } else {
        toast.error('فشل تغيير كلمة المرور');
      }
    } finally {
      setChangingPassword(false);
    }
  };

  const connectGoogle = async () => {
    try {
      const response = await axios.get(`${API}/auth/google/login`);
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error('فشل الاتصال بجوجل');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!settings) return null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">الإعدادات</h1>
        <p className="text-gray-500 mt-1">إدارة إعدادات النظام والتكاملات</p>
      </div>

      <Card data-testid="password-settings">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <Lock className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <CardTitle>كلمة المرور</CardTitle>
              <CardDescription>تغيير كلمة مرور النظام</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="current-password">كلمة المرور الحالية</Label>
            <Input
              id="current-password"
              data-testid="current-password-input"
              type="password"
              placeholder="أدخل كلمة المرور الحالية"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-password">كلمة المرور الجديدة</Label>
            <Input
              id="new-password"
              data-testid="new-password-input"
              type="password"
              placeholder="أدخل كلمة المرور الجديدة"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm-password">تأكيد كلمة المرور الجديدة</Label>
            <Input
              id="confirm-password"
              data-testid="confirm-password-input"
              type="password"
              placeholder="أعد إدخال كلمة المرور الجديدة"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>
          <Button
            data-testid="change-password-btn"
            onClick={handleChangePassword}
            disabled={changingPassword}
            className="w-full bg-red-600 hover:bg-red-700"
          >
            {changingPassword ? (
              <>
                <Loader2 className="w-4 h-4 ml-2 animate-spin" />
                جاري التغيير...
              </>
            ) : (
              'تغيير كلمة المرور'
            )}
          </Button>
        </CardContent>
      </Card>

      <Card data-testid="telegram-settings">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Send className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <CardTitle>Telegram Bot</CardTitle>
              <CardDescription>إعدادات إشعارات Telegram</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="bot-token">Bot Token</Label>
            <Input
              id="bot-token"
              data-testid="telegram-token-input"
              type="password"
              placeholder="أدخل Bot Token من @BotFather"
              value={settings.telegram_bot_token || ''}
              onChange={(e) => setSettings({ ...settings, telegram_bot_token: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="chat-id">Chat ID</Label>
            <Input
              id="chat-id"
              data-testid="telegram-chatid-input"
              type="text"
              placeholder="أدخل Chat ID الخاص بك"
              value={settings.telegram_chat_id || ''}
              onChange={(e) => setSettings({ ...settings, telegram_chat_id: e.target.value })}
            />
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-700">
              ℹ️ للحصول على Bot Token: قم بمراسلة @BotFather على Telegram واستخدم أمر /newbot
            </p>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="google-calendar-settings">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <CardTitle>Google Calendar</CardTitle>
              <CardDescription>مزامنة مواعيد الجلسات</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {settings.google_tokens ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <div>
                <p className="font-medium text-green-900">متصل بنجاح</p>
                <p className="text-sm text-green-700">تم ربط حساب Google Calendar بنجاح</p>
              </div>
            </div>
          ) : (
            <div>
              <Button
                data-testid="connect-google-btn"
                onClick={connectGoogle}
                className="w-full gap-2 bg-green-600 hover:bg-green-700"
              >
                <Calendar className="w-5 h-5" />
                ربط مع Google Calendar
              </Button>
              <p className="text-sm text-gray-500 mt-3">
                ℹ️ سيتم إنشاء أحداث تلقائياً لجميع الجلسات القادمة
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card data-testid="auto-update-settings">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <SettingsIcon className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <CardTitle>التحديث التلقائي</CardTitle>
              <CardDescription>إعدادات تحديث القضايا</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="auto-update" className="text-base">تفعيل التحديث التلقائي</Label>
              <p className="text-sm text-gray-500 mt-1">تحديث بيانات القضايا بشكل دوري</p>
            </div>
            <Switch
              id="auto-update"
              data-testid="auto-update-switch"
              checked={settings.auto_update_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, auto_update_enabled: checked })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="frequency">تردد التحديث</Label>
            <select
              id="frequency"
              data-testid="update-frequency"
              value={settings.update_frequency || 'daily'}
              onChange={(e) => setSettings({ ...settings, update_frequency: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-4 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={!settings.auto_update_enabled}
            >
              <option value="hourly">كل ساعة</option>
              <option value="daily">يومياً</option>
              <option value="weekly">أسبوعياً</option>
            </select>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-3">
        <Button
          variant="outline"
          onClick={fetchSettings}
          disabled={saving}
        >
          إلغاء
        </Button>
        <Button
          data-testid="save-settings-btn"
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {saving ? 'جاري الحفظ...' : 'حفظ الإعدادات'}
        </Button>
      </div>
    </div>
  );
}